/**
 * CROEV Quiz V1 - branching engine
 * OF·27 Bioscience - Hair Routine Quiz
 *
 * - gated steps: a block renders only when its data-gate matches the answers
 *   (gender tracks: woman | man, nothing else; the reflection variant)
 * - derived keys: _track, _reflection, _result are computed from answers
 *   and can be used in gates like any question id
 * - every path reaches the result and the buy box
 * - loading step with a determinate ring, ticking checklist and review marquee
 * - reveal of the purchase sections only from the result's commit button
 *
 * Answer data stays in memory. Nothing is written to storage, no pixel
 * receives an answer value. One CustomEvent per step change carries the
 * step id only.
 *
 * 2026-09-19: the root now also carries data-reflection (photos | hairline |
 * record), the same derived value the result step already used for gating,
 * so the result step's CSS can show the one tracking figure that matches how
 * this visitor reflects on their own hair (croev-quiz-v1-result.liquid).
 *
 * 2026-09-19 (second pass): fillProfile now reads .cq__match-v (the result
 * step's profile tiles replaced the old <dl> rows) and falls back to <dd> so
 * nothing else regresses. Added initTimelineDraw(): the new inline-SVG
 * 12-week timeline draws in once, the first time it scrolls into view
 * (IntersectionObserver), skipped under prefers-reduced-motion.
 */
(function () {
  'use strict';

  var EXCLUSIVE = { none: 1, unsure: 1 };
  var BASE_TOTAL = 18;
  var PLACEHOLDER_KEYS = { problem_areas: 'problem_areas', duration: 'change_duration', priority: 'decision_priority', tried: 'previous_solutions', moment: 'felt_moment', fit: 'routine_fit', age: 'age', avoid: 'avoid', life: 'life_stage' };

  function CroevQuiz(root) {
    this.root = root;
    this.stage = root.querySelector('[data-cq-stage]');
    this.bar = root.querySelector('[data-cq-footerbar]');
    this.track = root.querySelector('[data-cq-track]');
    this.steps = Array.prototype.slice.call(root.querySelectorAll('.cq__step'));
    this.timers = [];
    var tot = parseInt(root.dataset.totalSteps, 10);
    if (tot > 0) BASE_TOTAL = tot;
    this.cacheTemplates();
    this.reset(true);
    this.bind();
  }

  CroevQuiz.prototype.clearTimers = function () {
    this.timers.forEach(function (t) { clearInterval(t); clearTimeout(t); });
    this.timers = [];
  };

  CroevQuiz.prototype.reset = function (silent) {
    this.answers = {};
    this.labels = {};
    this.derived = {};
    this.visited = 0;
    this.total = BASE_TOTAL;
    this.current = -1;
    this.revealed = false;
    this.busy = false;
    this.clearTimers();
    document.body.classList.remove('quiz-revealed');
    this.root.classList.remove('cq--done');
    var self = this;
    this.steps.forEach(function (st) {
      st.classList.remove('is-active');
      st.querySelectorAll('.cq__opt').forEach(function (o) {
        o.classList.remove('is-selected');
        o.setAttribute('aria-pressed', 'false');
      });
      var btn = st.querySelector('.cq__btn.is-loading');
      if (btn) { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); var l = btn.querySelector('[data-cq-label]'); if (l && l.dataset.orig) l.textContent = l.dataset.orig; }
      self.returnCta(st);
    });
    if (!silent) this.start();
  };

  CroevQuiz.prototype.start = function () {
    this.goTo(this.findNext(-1));
  };

  /* ---------- gating ---------- */

  CroevQuiz.prototype.valueOf = function (key) {
    if (key.charAt(0) === '_') return this.derived[key];
    return this.answers[key];
  };

  CroevQuiz.prototype.matches = function (key, wanted) {
    var have = this.valueOf(key);
    if (have == null) return false;
    var list = Array.isArray(have) ? have : [have];
    for (var i = 0; i < list.length; i++) {
      if (wanted.indexOf(list[i]) !== -1) return true;
    }
    return false;
  };

  CroevQuiz.prototype.gatePasses = function (gate) {
    if (!gate) return true;
    var clauses = gate.split('|');
    for (var i = 0; i < clauses.length; i++) {
      var c = clauses[i].trim();
      if (!c) continue;
      var idx = c.indexOf(':');
      if (idx === -1) continue;
      var key = c.slice(0, idx).trim();
      var vals = c.slice(idx + 1).split(',').map(function (v) { return v.trim(); });
      if (!this.matches(key, vals)) return false;
    }
    return true;
  };

  CroevQuiz.prototype.findNext = function (from) {
    for (var i = from + 1; i < this.steps.length; i++) {
      var st = this.steps[i];
      if (st.dataset.role === 'branch') continue;
      if (this.gatePasses(st.dataset.gate)) return i;
    }
    return -1;
  };

  CroevQuiz.prototype.findBranch = function (type, key) {
    for (var i = 0; i < this.steps.length; i++) {
      var st = this.steps[i];
      if (st.dataset.stepType === type && st.dataset.branchKey === key) return i;
    }
    return -1;
  };

  /* ---------- derived keys ---------- */

  CroevQuiz.prototype.derive = function () {
    var g = this.answers.gender;
    this.derived._track = g === 'woman' ? 'woman' : g === 'man' ? 'man' : undefined;

    var pa = this.answers.problem_areas || [];
    var fm = this.answers.felt_moment;
    if (this.derived._track && (pa.length || fm)) {
      var photoConcern = pa.indexOf('part_photo') !== -1 || pa.indexOf('ponytail') !== -1;
      var photoMoment = fm === 'overhead_photo' || fm === 'tying_back';
      if (this.derived._track === 'woman' && (photoConcern || photoMoment)) this.derived._reflection = 'photos';
      else if (this.derived._track === 'man') this.derived._reflection = 'hairline';
      else this.derived._reflection = 'record';
    }

    var p = this.answers.decision_priority;
    var t = this.derived._track;
    var r;
    if (p && t) {
      if (p === 'cost') r = 'any_cost';
      else if (t === 'man') {
        r = { nonhormonal: 'man_nonhormonal', routine: 'man_routine', evidence: 'man_evidence', photo: 'man_photo', private: 'gen_private' }[p];
      } else if (t === 'woman') {
        r = { photo: 'woman_photo', shedding: 'woman_shedding', nonhormonal: 'gen_nonhormonal', routine: 'gen_routine', evidence: 'gen_evidence' }[p];
      }
    }
    this.derived._result = r;

    var tried = this.answers.previous_solutions || [];
    this.derived._tried = tried.length ? (tried.length === 1 && tried[0] === 'none' ? 'no' : 'yes') : undefined;

    if (this.derived._track) { this.root.setAttribute('data-track', this.derived._track); } else { this.root.removeAttribute('data-track'); }
    if (this.derived._reflection) { this.root.setAttribute('data-reflection', this.derived._reflection); } else { this.root.removeAttribute('data-reflection'); }
  };

  /* ---------- navigation ---------- */

  CroevQuiz.prototype.goTo = function (idx) {
    if (idx < 0 || idx >= this.steps.length) return;
    var prev = this.steps[this.current];
    if (prev) { prev.classList.remove('is-active'); this.returnCta(prev); }
    this.clearTimers();
    this.current = idx;
    var st = this.steps[idx];
    st.classList.add('is-active');
    this.visited += 1;
    var skip = this.root.querySelector('[data-cq-skip]');
    if (skip) skip.hidden = this.visited > 1;
    this.fillReflect(st);
    this.fillTemplates(st);
    this.moveCta(st);
    this.updateProgress(false);
    window.scrollTo({ top: 0, behavior: 'auto' });
    var focusable = st.querySelector('.cq__title');
    if (focusable) { focusable.setAttribute('tabindex', '-1'); focusable.focus({ preventScroll: true }); }
    if (st.dataset.stepType === 'loading') this.runLoading(st);
    if (st.dataset.stepType === 'proof') this.runProof(st);
    try {
      this.root.dispatchEvent(new CustomEvent('croev:quiz:step', { bubbles: true, detail: { step: st.dataset.stepKey, type: st.dataset.stepType } }));
    } catch (e) { /* no-op */ }
  };

  CroevQuiz.prototype.advance = function () {
    if (this.revealed) return;
    var next = this.findNext(this.current);
    if (next === -1) { this.showResult(); return; }
    this.goTo(next);
  };

  CroevQuiz.prototype.showResult = function () {
    var key = this.derived._result;
    var idx = this.findBranch('result_step', key);
    if (idx === -1) idx = this.findBranch('result_step', 'gen_routine');
    if (idx === -1) return;
    var st = this.steps[idx];
    this.fillChips(st.querySelector('[data-cq-chips]'));
    this.fillBecause(st);
    this.fillProfile(st);
    this.fillAvoid(st);
    this.goTo(idx);
  };

  CroevQuiz.prototype.chipItems = function () {
    var items = [];
    (this.labels.problem_areas || []).forEach(function (l) { items.push(l); });
    return items;
  };

  CroevQuiz.prototype.fillChips = function (box) {
    if (!box) return;
    box.innerHTML = '';
    var items = this.chipItems();
    if (this.labels.change_duration && box.dataset.withDuration != null) items.push(this.labels.change_duration);
    items.forEach(function (l) {
      var chip = document.createElement('span');
      chip.className = 'cq__chip';
      chip.textContent = l;
      box.appendChild(chip);
    });
    box.hidden = items.length === 0;
  };

  CroevQuiz.prototype.fillReflect = function (st) {
    var line = st.querySelector('[data-cq-reflect]');
    if (!line) return;
    var box = line.querySelector('[data-cq-chips]');
    var src = line.dataset.cqReflectSource || 'problem_areas';
    if (src === 'problem_areas') this.fillChips(box);
    else this.fillChipsFrom(box, src);
    line.hidden = !box || box.hidden;
  };

  CroevQuiz.prototype.fillChipsFrom = function (box, qid) {
    if (!box) return;
    box.innerHTML = '';
    var l = this.labels[qid];
    var items = l == null ? [] : (Array.isArray(l) ? l : [l]);
    items.forEach(function (x) {
      var chip = document.createElement('span');
      chip.className = 'cq__chip';
      chip.textContent = x;
      box.appendChild(chip);
    });
    box.hidden = items.length === 0;
  };

  CroevQuiz.prototype.fillBecause = function (st) {
    var wrap = st.querySelector('[data-cq-because]');
    if (!wrap) return;
    var box = wrap.querySelector('[data-cq-chips-priority]');
    this.fillChipsFrom(box, 'decision_priority');
    wrap.hidden = !box || box.hidden;
  };

  CroevQuiz.prototype.fillProfile = function (st) {
    var self = this;
    var card = st.querySelector('[data-cq-profile]');
    if (!card) return;
    var shown = 0;
    card.querySelectorAll('[data-cq-profile-row]').forEach(function (row) {
      var v = self.joinLabels(row.dataset.cqProfileRow, 'list');
      var val = row.querySelector('.cq__match-v, dd');
      if (v && val) { val.textContent = v; row.hidden = false; shown += 1; }
      else row.hidden = true;
    });
    card.hidden = shown === 0;
  };

  CroevQuiz.prototype.fillAvoid = function (st) {
    var wrap = st.querySelector('[data-cq-avoid]');
    if (!wrap) return;
    var list = wrap.querySelector('[data-cq-avoid-list]');
    var vals = this.answers.avoid || [];
    var labs = this.labels.avoid || [];
    var ds = this.root.dataset;
    list.innerHTML = '';
    var n = 0;
    vals.forEach(function (v, i) {
      var key = 'avoid' + v.charAt(0).toUpperCase() + v.slice(1);
      var fact = ds[key];
      if (!fact) return;
      var li = document.createElement('li');
      var b = document.createElement('strong');
      b.className = 'cq__avoid-lab';
      b.textContent = labs[i] || v;
      li.appendChild(b);
      li.appendChild(document.createTextNode(' '));
      var s = document.createElement('span');
      s.textContent = fact;
      li.appendChild(s);
      list.appendChild(li);
      n += 1;
    });
    wrap.hidden = n === 0;
  };

  /* ---------- placeholders: {problem_areas} {duration} {priority} {tried} {moment} {fit} ---------- */

  CroevQuiz.prototype.cacheTemplates = function () {
    document.querySelectorAll('[data-cq-fill]').forEach(function (el) {
      if (el.__cqTpl == null) el.__cqTpl = el.textContent;
    });
  };

  CroevQuiz.prototype.joinLabels = function (qid, inline) {
    var l = this.labels[qid];
    if (l == null) return '';
    var arr = Array.isArray(l) ? l.slice() : [l];
    arr = arr.map(function (x) {
      var s = String(x).replace(/\.$/, '');
      if (inline === true && !/^I(\b|')/.test(s)) s = s.charAt(0).toLowerCase() + s.slice(1);
      return s;
    });
    if (arr.length <= 1) return arr.join('');
    if (inline === 'list') return arr.join(', ');
    if (arr.length === 2) return arr[0] + ' and ' + arr[1];
    return arr.slice(0, -1).join(', ') + ', and ' + arr[arr.length - 1];
  };

  CroevQuiz.prototype.fillTemplates = function (scope) {
    var self = this;
    var esc = function (s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); };
    (scope || document).querySelectorAll('[data-cq-fill]').forEach(function (el) {
      var tpl = el.__cqTpl != null ? el.__cqTpl : el.textContent;
      if (tpl.indexOf('{') === -1) return;
      var html = esc(tpl).replace(/\{(\w+)\}/g, function (m, key) {
        var qid = PLACEHOLDER_KEYS[key];
        if (!qid) return m;
        var v = self.joinLabels(qid, true);
        if (!v) return '';
        return '<strong class="cq__hl">' + esc(v) + '</strong>';
      });
      el.innerHTML = html.replace(/\s{2,}/g, ' ');
    });
  };

  /* ---------- proof step: bars fill and numbers count up on entry ---------- */

  CroevQuiz.prototype.runProof = function (st) {
    var self = this;
    var duration = 1100;
    var bars = Array.prototype.slice.call(st.querySelectorAll('[data-cq-bar]'));
    var counts = Array.prototype.slice.call(st.querySelectorAll('[data-cq-count]'));
    var people = Array.prototype.slice.call(st.querySelectorAll('.cq__person.is-on'));
    bars.forEach(function (b) { b.style.width = '0%'; });
    people.forEach(function (p) { p.classList.remove('is-lit'); });
    var start = Date.now();
    var ease = function (x) { return 1 - Math.pow(1 - x, 3); };
    var timer = setInterval(function () {
      var p = Math.min(1, (Date.now() - start) / duration);
      var e = ease(p);
      bars.forEach(function (b) { b.style.width = (parseFloat(b.style.getPropertyValue('--v')) * e) + '%'; });
      counts.forEach(function (c) { c.textContent = Math.round(parseFloat(c.dataset.to) * e) + (c.dataset.suffix || ''); });
      people.forEach(function (ph, i) { if (e >= (i + 1) / (people.length + 0.2)) ph.classList.add('is-lit'); });
      if (p >= 1) { clearInterval(timer); people.forEach(function (ph) { ph.classList.add('is-lit'); }); }
    }, 40);
    this.timers.push(timer);
  };

  /* ---------- loading step: determinate ring, ticking checklist ---------- */

  CroevQuiz.prototype.runLoading = function (st) {
    var self = this;
    var duration = parseInt(st.dataset.duration, 10) || 4000;
    var ring = st.querySelector('[data-cq-ring]');
    var pctEl = st.querySelector('[data-cq-pct]');
    var items = Array.prototype.slice.call(st.querySelectorAll('[data-cq-check]'));
    var circ = ring ? parseFloat(ring.dataset.circ || '0') : 0;
    var start = Date.now();
    items.forEach(function (i) { i.classList.remove('is-done'); });
    if (ring) ring.style.strokeDashoffset = circ;
    var timer = setInterval(function () {
      var p = Math.min(1, (Date.now() - start) / duration);
      if (ring) ring.style.strokeDashoffset = circ * (1 - p);
      if (pctEl) pctEl.textContent = Math.round(p * 100) + '%';
      items.forEach(function (it, i) {
        if (p >= (i + 1) / (items.length + 0.5)) it.classList.add('is-done');
      });
      if (p >= 1) {
        clearInterval(timer);
        items.forEach(function (it) { it.classList.add('is-done'); });
        var t = setTimeout(function () { self.advance(); }, 450);
        self.timers.push(t);
      }
    }, 50);
    this.timers.push(timer);
  };

  /* ---------- commit + reveal ---------- */

  CroevQuiz.prototype.commit = function (btn) {
    if (this.revealed || this.busy) return;
    this.busy = true;
    var self = this;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    var label = btn.querySelector('[data-cq-label]');
    var wait = btn.dataset.waitText || 'Preparing your routine…';
    if (label) { label.dataset.orig = label.textContent; label.textContent = wait; }
    var t = setTimeout(function () {
      self.revealed = true;
      self.updateProgress(true);
      self.fillTemplates(document);
      document.body.classList.add('quiz-revealed');
      self.root.classList.add('cq--done');
      self.returnCta(self.steps[self.current]);
      window.scrollTo({ top: 0, behavior: 'auto' });
      self.busy = false;
    }, 1000);
    this.timers.push(t);
  };

  /* ---------- CTA relocation (fixed bar lives outside the transformed stage) ---------- */

  CroevQuiz.prototype.moveCta = function (st) {
    if (!this.bar) return;
    var wrap = st.querySelector('.cq__cta-wrap');
    this.bar.innerHTML = '';
    if (!wrap) { this.bar.hidden = true; return; }
    wrap.__cqHome = st;
    this.bar.appendChild(wrap);
    this.bar.hidden = false;
    this.syncContinue(st);
  };

  CroevQuiz.prototype.returnCta = function (st) {
    if (!this.bar || !st) return;
    var wrap = this.bar.querySelector('.cq__cta-wrap');
    if (wrap && wrap.__cqHome === st) {
      var home = st.querySelector('[data-cq-cta-home]') || st;
      home.appendChild(wrap);
      wrap.__cqHome = null;
    }
    this.bar.hidden = true;
  };

  CroevQuiz.prototype.syncContinue = function (st) {
    var btn = this.bar ? this.bar.querySelector('[data-action="continue"]') : null;
    if (!btn) return;
    if (st.dataset.stepType !== 'question') { btn.disabled = false; return; }
    btn.disabled = st.querySelectorAll('.cq__opt.is-selected').length === 0;
  };

  /* ---------- progress ---------- */

  CroevQuiz.prototype.updateProgress = function (done) {
    if (!this.track) return;
    var pct = done ? 100 : Math.min(100, Math.round((this.visited / this.total) * 100));
    this.track.style.transform = 'scaleX(' + (pct / 100) + ')';
    var pb = this.track.parentNode;
    if (pb) pb.setAttribute('aria-valuenow', pct);
  };

  /* ---------- answers ---------- */

  CroevQuiz.prototype.selectOption = function (opt, st) {
    var qid = st.dataset.questionId;
    var multi = st.dataset.selection === 'multi';
    var opts = Array.prototype.slice.call(st.querySelectorAll('.cq__opt'));
    var val = opt.dataset.value;

    if (!multi) {
      if (this.busy) return;
      opts.forEach(function (o) { o.classList.remove('is-selected'); o.setAttribute('aria-pressed', 'false'); });
      opt.classList.add('is-selected');
      opt.setAttribute('aria-pressed', 'true');
      this.answers[qid] = val;
      this.labels[qid] = opt.dataset.label;
      this.derive();
      var self = this;
      this.busy = true;
      var t = setTimeout(function () { self.busy = false; self.advance(); }, 220);
      this.timers.push(t);
      return;
    }

    var on = !opt.classList.contains('is-selected');
    if (on && EXCLUSIVE[val]) {
      opts.forEach(function (o) { o.classList.remove('is-selected'); o.setAttribute('aria-pressed', 'false'); });
    } else if (on) {
      opts.forEach(function (o) {
        if (EXCLUSIVE[o.dataset.value]) { o.classList.remove('is-selected'); o.setAttribute('aria-pressed', 'false'); }
      });
    }
    opt.classList.toggle('is-selected', on);
    opt.setAttribute('aria-pressed', on ? 'true' : 'false');
    var vals = [], labs = [];
    opts.forEach(function (o) {
      if (o.classList.contains('is-selected')) { vals.push(o.dataset.value); labs.push(o.dataset.label); }
    });
    if (vals.length) { this.answers[qid] = vals; this.labels[qid] = labs; }
    else { delete this.answers[qid]; delete this.labels[qid]; }
    this.derive();
    this.syncContinue(st);
  };

  /* ---------- events: one delegated listener on the root ---------- */

  CroevQuiz.prototype.bind = function () {
    var self = this;
    this.root.addEventListener('click', function (e) {
      var opt = e.target.closest('.cq__opt');
      if (opt && self.root.contains(opt)) {
        var st = opt.closest('.cq__step');
        if (st && st.classList.contains('is-active')) self.selectOption(opt, st);
        return;
      }
      var btn = e.target.closest('[data-action]');
      if (!btn || !self.root.contains(btn)) return;
      var action = btn.dataset.action;
      var active = self.steps[self.current];
      if (!active) return;
      if (action === 'continue') {
        if (btn.disabled) return;
        if (active.dataset.stepType === 'question' && !self.answers[active.dataset.questionId]) return;
        self.advance();
      } else if (action === 'commit') {
        self.commit(btn);
      }
    });

    this.root.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var opt = e.target.closest('.cq__opt');
      if (opt) { e.preventDefault(); opt.click(); }
    });

    window.addEventListener('pageshow', function (e) {
      if (e.persisted) self.reset(false);
    });
  };

  /* ---------- plan switch inside the rebuilt buy box ---------- */

  function bindBuybox(box) {
    var plans = box.querySelectorAll('[data-cq-plan]');
    var btn = box.querySelector('[data-cq-buy]');
    var label = box.querySelector('[data-cq-buy-label]');
    var price = box.querySelector('[data-cq-buy-price]');
    var gnote = box.querySelector('[data-cq-guarantee-note]');
    var snote = box.querySelector('[data-cq-standard-note]');
    function apply(p) {
      plans.forEach(function (x) { x.classList.toggle('is-on', x === p); var r = x.querySelector('input'); if (r) r.checked = x === p; });
      if (btn) btn.setAttribute('href', p.dataset.url);
      if (label) label.textContent = p.dataset.cta;
      if (price) price.textContent = p.dataset.price;
      var adv = p.dataset.plan === 'advanced';
      if (gnote) gnote.hidden = !adv;
      if (snote) snote.hidden = adv;
    }
    plans.forEach(function (p) {
      p.addEventListener('click', function () { apply(p); });
      var r = p.querySelector('input');
      if (r) r.addEventListener('change', function () { apply(p); });
    });
    var initial = box.querySelector('[data-cq-plan].is-on') || plans[0];
    if (initial) apply(initial);

    var main = box.querySelector('[data-cq-gal-main]');
    box.querySelectorAll('[data-cq-thumb]').forEach(function (t) {
      t.addEventListener('click', function () {
        if (main) { main.src = t.dataset.src; main.alt = t.dataset.alt || ''; }
        box.querySelectorAll('[data-cq-thumb]').forEach(function (x) { x.setAttribute('aria-current', 'false'); });
        t.setAttribute('aria-current', 'true');
      });
    });
  }

  /* ---------- result step: the 12-week timeline draws in once, on view ---------- */
  function initTimelineDraw() {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.querySelectorAll('.cq__tl:not([data-tl-bound])').forEach(function (el) {
      el.setAttribute('data-tl-bound', '1');
      if (reduce || typeof IntersectionObserver === 'undefined') { el.classList.add('is-drawn'); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { entry.target.classList.add('is-drawn'); io.unobserve(entry.target); }
        });
      }, { threshold: 0.3 });
      io.observe(el);
    });
  }

  function init() {
    document.querySelectorAll('[data-cq-root]').forEach(function (r) {
      if (r.__cq) return;
      r.__cq = new CroevQuiz(r);
      r.__cq.start();
    });
    document.querySelectorAll('[data-cq-buybox]').forEach(function (b) {
      if (b.__cq) return;
      b.__cq = true;
      bindBuybox(b);
    });
    initTimelineDraw();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', init);
})();
